import { network } from '@root/utils'
import { handleReturn, QueryFunction, MutationFunction } from './root'

export enum AuditTrailItemType {
  COMMENT = 'comment',
  ACTIVITY = 'activity',
  HISTORY_BEGIN = 'history_begin'
}

interface IAuditTrailCommentItem {
  type: AuditTrailItemType.COMMENT
  item: IAuditTrailComment
  time: string
  message: string
}

interface IAuditTrailActivityItem {
  type: AuditTrailItemType.ACTIVITY
  item: IAuditTrailObjectBase<IChangeDocument>
  time: string
  message: string
}

interface IAuditTrailHistoryBeginItem {
  type: AuditTrailItemType.HISTORY_BEGIN
  time: string
  message: string
}

export type IAuditTrailWrapper =
  | IAuditTrailCommentItem
  | IAuditTrailActivityItem
  | IAuditTrailHistoryBeginItem

export interface IAuditTrail extends Array<IAuditTrailWrapper> {}

export enum AuditTrailChangeReasons {
  IMPORT_TASK = 'IMPORT_TASK',
  MOCK_SUGGESTION = 'MOCK_SUGGESTION',
  ENABLE_BANKING_IMPORTED = 'ENABLE_BANKING_IMPORTED',
  HOLVI_IMPORTED = 'HOLVI_IMPORTED',
  FROM_SUGGESTION = 'FROM_SUGGESTION',
  INVOICE_IMPORTED = 'INVOICE_IMPORTED',
  FROM_RULE_MATCH = 'FROM_RULE_MATCH',
  RULE_MATCH_WAIT_ATTACHMENT = 'RULE_MATCH_WAIT_ATTACHMENT',
  FROM_RULE_MATCH_AFTER_ATTACHMENT = 'FROM_RULE_MATCH_AFTER_ATTACHMENT'
}

export enum AuditTrailFields {
  BLUEPRINT = 'blueprint',
  ATTACHMENTS = 'attachments',
  RELATIONS = 'relations',
  CONTACT = 'contact',
  DESCRIPTION = 'description',
  DATE = 'date',
  IS_FLAGGED = 'is_flagged',
  IS_LOCKED = 'is_locked',
  TAGS = 'tags',
  RULE = 'rule'
}

export enum AuditTrailChangeType {
  CREATE = '+',
  DELETE = '-',
  UPDATE = '~'
}

export interface IAuditUser {
  id?: number
  name?: string
  email?: string
  client?: string | null
}

export const LUCA_AUDIT_CLIENT = 'nocfo-agent'

export interface IAuditTrailWorkflowOrigin {
  id?: number | null
  name: string
}

export interface IAuditTrailObjectBase<T> {
  history_id: number
  changed_by: IAuditUser
  type: AuditTrailChangeType
  change_time: string
  change_reason: AuditTrailChangeReasons
  changes: T[]
}

export interface IChangeDocument {
  field: AuditTrailFields
  old_values: object[] | Record<string, any>[] | Record<string, any>
  new_values: object[] | Record<string, any>[] | Record<string, any>
  added?: object[] | Record<string, any>[]
  removed?: object[] | Record<string, any>[]
  changed?: object[] | Record<string, any>[]
}

interface IDocumentAuditTrailProps {
  documentId: number
  slug: string
}

export const fetchDocumentAuditTrail: QueryFunction<
  IDocumentAuditTrailProps,
  IAuditTrail
> = async ({ slug, documentId }) => {
  const url = `/v1/business/${slug}/document/${documentId}/audit_trail/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface IAuditTrailComment {
  id: number
  created_at: string
  updated_at: string
  text: string
  document: number
  updated_by: IAuditUser
  created_by: IAuditUser
  created_by_workflow?: IAuditTrailWorkflowOrigin | null
}
interface AuditTrailDocumentCommentCreateProps {
  documentId: number
  slug: string
}

export const createAuditTrailDocumentComment: MutationFunction<
  AuditTrailDocumentCommentCreateProps,
  IAuditTrailComment
> = async ({ slug, documentId }, comment) => {
  const url = `/v1/business/${slug}/document/${documentId}/audit_trail/comment/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    data: { text: comment.text, document: documentId },
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

interface AuditTrailDocumentCommentProps {
  slug: string
  commentId: number
}

export const updateAuditTrailDocumentComment: MutationFunction<
  AuditTrailDocumentCommentProps,
  IAuditTrailComment
> = async ({ slug, commentId }, comment) => {
  const url = `/v1/business/${slug}/audit_trail/comment/${commentId}/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'PATCH',
    data: comment,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const deleteAuditTrailDocumentComment: MutationFunction<
  AuditTrailDocumentCommentProps,
  void
> = async ({ slug, commentId }) => {
  const url = `/v1/business/${slug}/audit_trail/comment/${commentId}/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'DELETE',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

interface IAuditTrailVisitedProps {
  slug: string
  documentId: number
}

export const documentAuditTrailVisited: MutationFunction<IAuditTrailVisitedProps, void> = async ({
  slug,
  documentId
}) => {
  const url = `/v1/business/${slug}/document/${documentId}/audit_trail/visited/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'PATCH',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}
